import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatDateTime, formatNOK } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Check,
  AlertTriangle,
  Calendar as CalIcon,
  ArrowLeft,
  Clock,
  Users,
  CalendarClock,
} from "lucide-react";
import FloatingThemeToggle from "@/components/FloatingThemeToggle";
import SEO from "@/components/SEO";
import { toast } from "sonner";

/**
 * Public booking management — accessed via the cancel_token e-mail link.
 * Customer can either:
 *  - Cancel the booking outright (existing flow)
 *  - Reschedule to a new date/time and optionally change staff
 *
 * The cancel_token is rotated server-side after a successful reschedule;
 * we follow the redirect to the fresh token without forcing a hard reload
 * so the rest of the UI state stays in sync.
 */
export default function CancelBooking() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [working, setWorking] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  // mode: "menu" | "cancel" | "reschedule" | "rescheduled"
  const [mode, setMode] = useState("menu");

  // Reschedule form state
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");
  // "KEEP" = same staff as before, "ANY" = "hvem som helst" (clear), or user.id
  const [staffChoice, setStaffChoice] = useState("KEEP");
  const [busy, setBusy] = useState({ list: [], closed: false, closedReason: "" });
  const [busyLoading, setBusyLoading] = useState(false);

  const load = async (t = token) => {
    try {
      const res = await api.get(`/public/booking/${t}`);
      setData(res.data);
      if (res.data.booking?.status === "cancelled") {
        setCancelled(true);
        setMode("cancel");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Lenken er ugyldig eller utløpt");
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [token]);

  const booking = data?.booking;
  const business = data?.business;
  const team = data?.team || [];
  const showStaffPicker = team.length > 1;

  // Fetch busy intervals when the customer picks a date / staff combo.
  useEffect(() => {
    if (mode !== "reschedule" || !newDate || !business?.slug) return;
    (async () => {
      setBusyLoading(true);
      try {
        const params = { date: newDate };
        // Map staffChoice → staff_id query param. KEEP = original staff if set; ANY = no filter.
        let staffIdForBusy = null;
        if (staffChoice === "KEEP" && booking?.staff_id) staffIdForBusy = booking.staff_id;
        else if (staffChoice !== "KEEP" && staffChoice !== "ANY") staffIdForBusy = staffChoice;
        if (staffIdForBusy) params.staff_id = staffIdForBusy;
        const res = await api.get(`/public/business/${business.slug}/busy`, { params });
        setBusy({
          list: res.data.busy || [],
          closed: !!res.data.closed,
          closedReason: res.data.closed_reason || "",
        });
      } catch {
        setBusy({ list: [], closed: false, closedReason: "" });
      } finally {
        setBusyLoading(false);
      }
    })();
  }, [mode, newDate, staffChoice, business?.slug, booking?.staff_id]);

  // Build candidate slots from opening_hours for the picked date.
  // Mirrors PublicBooking.jsx convention: 30-min steps, weekday=0..6 (Mon..Sun).
  const timeSlots = useMemo(() => {
    if (!newDate || !business || !booking) return [];
    const d = new Date(newDate + "T00:00:00");
    const weekday = (d.getDay() + 6) % 7;
    const hours = (business.opening_hours || []).find((h) => h.day === weekday);
    if (!hours || hours.closed) return [];
    const slots = [];
    const [oh, om] = hours.open.split(":").map(Number);
    const [ch, cm] = hours.close.split(":").map(Number);
    const start = oh * 60 + om;
    const end = ch * 60 + cm;
    const duration = booking.duration_min || 30;
    for (let m = start; m + duration <= end; m += 30) {
      slots.push(
        `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`,
      );
    }
    return slots;
  }, [newDate, business, booking]);

  const isSlotBusy = (slotHHMM) => {
    if (!newDate || !booking) return false;
    const slotStart = new Date(`${newDate}T${slotHHMM}:00`);
    const slotEnd = new Date(slotStart.getTime() + (booking.duration_min || 30) * 60000);
    return busy.list.some((b) => {
      const bs = new Date(b.start_at);
      const be = new Date(b.end_at);
      return slotStart < be && slotEnd > bs;
    });
  };

  // Today as YYYY-MM-DD (local) for the date input min attribute.
  const today = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);

  const cancel = async () => {
    setWorking(true);
    try {
      await api.post(`/public/booking/${token}/cancel`);
      setCancelled(true);
      toast.success("Avbestilling bekreftet");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke avbestille");
    } finally {
      setWorking(false);
    }
  };

  const submitReschedule = async () => {
    if (!newDate || !newTime) {
      toast.error("Velg dato og tid");
      return;
    }
    setWorking(true);
    try {
      // Build ISO with the visitor's local timezone offset so the backend
      // gets an unambiguous instant. The server converts to Europe/Oslo
      // when comparing against opening hours and closed dates.
      const localIso = new Date(`${newDate}T${newTime}:00`).toISOString();
      const body = { start_at: localIso };
      // staff_id semantics:
      //   "KEEP" -> omit the key (server keeps existing staff_id)
      //   "ANY"  -> send empty string (server clears staff_id)
      //   "<id>" -> send the id
      if (staffChoice === "ANY") body.staff_id = "";
      else if (staffChoice !== "KEEP") body.staff_id = staffChoice;
      const res = await api.post(`/public/booking/${token}/reschedule`, body);
      const newToken = res.data?.booking?.cancel_token;
      toast.success("Timen din er endret");
      setMode("rescheduled");
      // Swap the URL to the fresh token so a refresh keeps working.
      if (newToken && newToken !== token) {
        navigate(`/avbestill/${newToken}`, { replace: true });
      } else {
        load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke endre time");
    } finally {
      setWorking(false);
    }
  };

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full rounded-2xl border-border" data-testid="cancel-error">
          <CardContent className="p-8 text-center">
            <div className="inline-flex w-14 h-14 rounded-2xl bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 items-center justify-center mb-3">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h1 className="font-heading text-xl font-bold">Ugyldig lenke</h1>
            <p className="text-muted-foreground mt-2">{error}</p>
            <Link to="/" className="text-primary mt-4 inline-block">
              ← Til forsiden
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  if (!data) return <p className="p-6 text-muted-foreground">Laster...</p>;

  const heading = cancelled
    ? "Avbestilt"
    : mode === "reschedule"
    ? "Endre time"
    : mode === "cancel"
    ? "Avbestill time"
    : mode === "rescheduled"
    ? "Ny tid bekreftet"
    : "Administrer time";

  return (
    <div
      className="min-h-screen bg-muted/40 flex items-center justify-center p-4"
      data-testid="cancel-page"
    >
      <SEO title="Administrer time — ClientFlow" path="/avbestill" noindex />
      <FloatingThemeToggle />
      <Card className="max-w-md w-full rounded-2xl border-border anim-in">
        <CardContent className="p-6 sm:p-8">
          <Link
            to="/"
            className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4"
          >
            <ArrowLeft className="w-4 h-4" /> ClientFlow
          </Link>
          <div className="flex items-center gap-3 mb-4">
            {business?.logo_url ? (
              <img
                src={business.logo_url}
                alt="Logo"
                className="w-12 h-12 rounded-xl object-cover border border-border"
              />
            ) : (
              <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center text-primary">
                <CalIcon className="w-5 h-5" />
              </div>
            )}
            <div className="min-w-0">
              <p className="font-heading font-semibold truncate">{business?.name}</p>
              {business?.phone && (
                <p className="text-xs text-muted-foreground">{business.phone}</p>
              )}
            </div>
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight" data-testid="manage-heading">
            {heading}
          </h1>

          {/* Booking summary always visible */}
          <div className="mt-5 p-4 rounded-xl bg-accent text-sm space-y-1" data-testid="booking-summary">
            <p>
              <span className="font-semibold">Tjeneste:</span> {booking.service_name}
            </p>
            <p>
              <span className="font-semibold">Tid:</span> {formatDateTime(booking.start_at)}
            </p>
            <p>
              <span className="font-semibold">Pris:</span> {formatNOK(booking.price_nok)}
            </p>
            <p>
              <span className="font-semibold">Navn:</span> {booking.customer_name}
            </p>
            {booking.staff_name && (
              <p>
                <span className="font-semibold">Hos:</span> {booking.staff_name}
              </p>
            )}
          </div>

          {/* CANCELLED state — terminal */}
          {cancelled && (
            <div
              className="mt-6 p-4 rounded-xl bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 text-sm flex items-center gap-2"
              data-testid="cancel-confirmed"
            >
              <Check className="w-5 h-5" /> Timen er avbestilt. Velkommen tilbake en annen gang!
            </div>
          )}

          {/* RESCHEDULED state — terminal-ish success */}
          {!cancelled && mode === "rescheduled" && (
            <div
              className="mt-6 p-4 rounded-xl bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 text-sm space-y-2"
              data-testid="reschedule-confirmed"
            >
              <p className="flex items-center gap-2 font-semibold">
                <Check className="w-5 h-5" /> Timen din er endret
              </p>
              <p className="text-xs">
                Ny bekreftelse er sendt på e-post hvis du oppga adresse. Lenken i den e-posten erstatter denne siden.
              </p>
            </div>
          )}

          {/* MENU — two big actions */}
          {!cancelled && mode === "menu" && (
            <div className="mt-6 grid gap-2">
              <Button
                onClick={() => setMode("reschedule")}
                className="rounded-xl h-auto py-3"
                data-testid="open-reschedule-btn"
              >
                <CalendarClock className="w-4 h-4 mr-2" /> Endre time
              </Button>
              <Button
                onClick={() => setMode("cancel")}
                variant="outline"
                className="rounded-xl h-auto py-3"
                data-testid="open-cancel-btn"
              >
                Avbestill time
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                Endringer er ikke mulig mindre enn 2 timer før timen — ring oss da.
              </p>
            </div>
          )}

          {/* CANCEL confirmation step */}
          {!cancelled && mode === "cancel" && (
            <>
              <p className="text-sm text-muted-foreground mt-5">
                Er du sikker på at du vil avbestille denne timen?
              </p>
              <div className="mt-4 flex flex-col gap-2">
                <Button
                  onClick={cancel}
                  disabled={working}
                  className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  data-testid="confirm-cancel-btn"
                >
                  {working ? "Avbestiller..." : "Bekreft avbestilling"}
                </Button>
                <Button
                  variant="outline"
                  className="rounded-xl w-full"
                  onClick={() => setMode("menu")}
                  data-testid="back-to-menu-from-cancel"
                >
                  Tilbake
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                Avbestillinger er ikke mulig mindre enn 2 timer før timen — ring oss da.
              </p>
            </>
          )}

          {/* RESCHEDULE flow */}
          {!cancelled && mode === "reschedule" && (
            <div className="mt-6 space-y-4" data-testid="reschedule-form">
              {/* Date picker */}
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                  <CalIcon className="w-3.5 h-3.5" /> Ny dato
                </label>
                <input
                  type="date"
                  min={today}
                  value={newDate}
                  onChange={(e) => {
                    setNewDate(e.target.value);
                    setNewTime("");
                  }}
                  className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="reschedule-date-input"
                />
              </div>

              {/* Staff picker — only if team has ≥2 members */}
              {showStaffPicker && (
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" /> Hos
                  </label>
                  <div className="mt-1 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setStaffChoice("KEEP");
                        setNewTime("");
                      }}
                      className={`text-xs rounded-xl border px-3 py-2 transition ${
                        staffChoice === "KEEP"
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background border-border hover:bg-accent"
                      }`}
                      data-testid="staff-choice-keep"
                    >
                      Samme som før{booking.staff_name ? ` (${booking.staff_name})` : ""}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setStaffChoice("ANY");
                        setNewTime("");
                      }}
                      className={`text-xs rounded-xl border px-3 py-2 transition ${
                        staffChoice === "ANY"
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background border-border hover:bg-accent"
                      }`}
                      data-testid="staff-choice-any"
                    >
                      Hvem som helst
                    </button>
                    {team.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => {
                          setStaffChoice(t.id);
                          setNewTime("");
                        }}
                        className={`text-xs rounded-xl border px-3 py-2 transition ${
                          staffChoice === t.id
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background border-border hover:bg-accent"
                        }`}
                        data-testid={`staff-choice-${t.id}`}
                      >
                        {t.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Time slot grid */}
              {newDate && (
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" /> Ny tid
                  </label>
                  {busy.closed ? (
                    <p
                      className="mt-2 text-sm text-amber-700 dark:text-amber-300"
                      data-testid="reschedule-closed-msg"
                    >
                      Stengt denne dagen
                      {busy.closedReason ? ` — ${busy.closedReason}` : ""}.
                    </p>
                  ) : timeSlots.length === 0 ? (
                    <p className="mt-2 text-sm text-muted-foreground">
                      Ingen ledige tidspunkter denne dagen.
                    </p>
                  ) : (
                    <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {timeSlots.map((s) => {
                        const taken = isSlotBusy(s);
                        const isSel = newTime === s;
                        return (
                          <button
                            key={s}
                            type="button"
                            disabled={taken}
                            onClick={() => setNewTime(s)}
                            className={`text-sm rounded-xl border px-2 py-2 transition ${
                              isSel
                                ? "bg-primary text-primary-foreground border-primary"
                                : taken
                                ? "bg-muted text-muted-foreground/50 border-border cursor-not-allowed line-through"
                                : "bg-background border-border hover:bg-accent"
                            }`}
                            data-testid={taken ? "reschedule-slot-busy" : "reschedule-slot"}
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {busyLoading && (
                    <p className="text-xs text-muted-foreground mt-2">Sjekker tilgjengelighet...</p>
                  )}
                </div>
              )}

              <div className="flex flex-col gap-2 pt-2">
                <Button
                  onClick={submitReschedule}
                  disabled={working || !newDate || !newTime}
                  className="rounded-xl"
                  data-testid="confirm-reschedule-btn"
                >
                  {working ? "Lagrer..." : "Bekreft ny tid"}
                </Button>
                <Button
                  variant="outline"
                  className="rounded-xl w-full"
                  onClick={() => {
                    setMode("menu");
                    setNewDate("");
                    setNewTime("");
                    setStaffChoice("KEEP");
                  }}
                  data-testid="back-to-menu-from-reschedule"
                >
                  Tilbake
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Endringer er ikke mulig mindre enn 2 timer før timen — ring oss da.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
